import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { useTranslation } from 'react-i18next';

export interface DropdownOption {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
}

interface ButtonWithDropdownProps {
  mainButtonLabel: string;
  mainButtonOnClick: () => void;
  dropdownOptions?: DropdownOption[];
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  loadingText?: string;
}

export const ButtonWithDropdown: React.FC<ButtonWithDropdownProps> = ({
  mainButtonLabel,
  mainButtonOnClick,
  dropdownOptions = [],
  className = '',
  disabled = false,
  isLoading = false,
  loadingText,
}) => {
  const { t } = useTranslation('pleri/shared');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mainButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const buttonWrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDropdownOptionClick = (onClick: () => void) => {
    setIsDropdownOpen(false);
    onClick();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  };

  const applyHoverEffect = () => {
    if (buttonWrapperRef.current) {
      buttonWrapperRef.current.style.boxShadow =
        'rgba(0,0,0,0.25) 0px 4px 4px 0px';
    }
  };

  const removeHoverEffect = () => {
    if (buttonWrapperRef.current) {
      buttonWrapperRef.current.style.boxShadow = 'none';
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        ref={buttonWrapperRef}
        className="flex"
        style={{
          boxShadow: 'none',
          borderRadius: '10px',
        }}
        onMouseEnter={applyHoverEffect}
        onMouseLeave={removeHoverEffect}
      >
        {/* Main Button */}
        <button
          ref={mainButtonRef}
          onClick={mainButtonOnClick}
          disabled={disabled || isLoading}
          className={`text-action-primary-text flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold transition-shadow focus:outline-none disabled:pointer-events-none ${
            isLoading
              ? 'bg-action-primary-disabled cursor-not-allowed'
              : 'bg-action-primary disabled:bg-action-primary-disabled'
          }`}
          style={{
            borderRadius: dropdownOptions.length > 0 ? '10px 0 0 10px' : '10px',
          }}
        >
          {isLoading && (
            <svg
              className="h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          )}
          {isLoading && loadingText ? loadingText : mainButtonLabel}
        </button>

        {/* Dropdown Button */}
        {dropdownOptions.length > 0 && (
          <button
            ref={dropdownButtonRef}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            onKeyDown={handleKeyDown}
            disabled={disabled || isLoading}
            className={`transition-background text-action-primary-text flex items-center justify-center p-2 focus:outline-none disabled:pointer-events-none ${
              isLoading
                ? 'bg-action-primary-disabled cursor-not-allowed'
                : 'bg-action-primary disabled:bg-action-primary-disabled'
            }`}
            style={{
              borderRadius: '0 10px 10px 0',
              opacity: '1',
            }}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              if (!disabled) {
                e.currentTarget.style.opacity = '1';
              }
            }}
            onMouseDown={(e) => {
              if (!disabled) {
                e.currentTarget.style.opacity = '0.75';
              }
            }}
            onMouseUp={(e) => {
              if (!disabled) {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            aria-label={t('openOptionsMenu')}
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
          >
            <ChevronDownIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      {/* Dropdown Menu */}
      {isDropdownOpen && dropdownOptions.length > 0 && (
        <div
          className="absolute right-0 z-50 w-56 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900"
          style={{ top: '100%', marginTop: '8px' }}
        >
          <div className="py-1">
            {dropdownOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <button
                  key={option.id}
                  onClick={() => handleDropdownOptionClick(option.onClick)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  <IconComponent className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="font-medium">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
